import type { ShareRoomPayload } from '~/api/types'
import { money } from '~/calendar/ListView'

import { Frame, Wordmark, link } from './Frame'
import { Line, Stop, meta } from './Line'
import s from './Share.module.css'

export type RoomProps = Omit<ShareRoomPayload, 'kind'>

/** Above this many characters the name steps down a size, and above the second, again. */
const NAME_FULL = 14
const NAME_MID = 24

/**
 * Format D: a room's coming nights, straight off its page, as stops on one line.
 *
 * The name is set large in the display face, the way the room's own sign would be; the
 * stops are the venue page's first four, the night in the mono left of the rail, the name
 * in the reading face, the time and the price in the mono beneath — and not the room,
 * which is the heading.
 */
export function Room({ host, frame, name, rows, slug }: RoomProps) {
  const size = name.length > NAME_MID ? s.long : name.length > NAME_FULL ? s.mid : ''
  return (
    <Frame format="D" frame={frame} className={s.room}>
      <div className={s.head}>
        <div className={`${s.kick} ${s.mono}`} data-probe="data">
          {rows.length === 0 ? 'Nothing listed yet' : `Coming up · ${rows.length} ${rows.length === 1 ? 'night' : 'nights'}`}
        </div>
        <div className={`${s.big} ${size} ${s.disp}`} data-probe="display">
          {name}
        </div>
      </div>
      <Line labelled>
        {rows.map(row => (
          <Stop key={row.id} day={`${row.dowShort} ${row.dayNum}`}>
            <div className={s.title}>{row.title}</div>
            <span className={`${s.meta} ${s.mono}`}>{meta(row, money(row), { venue: false })}</span>
          </Stop>
        ))}
      </Line>
      <div className={s.foot}>
        <div className={`${s.host} ${s.mono}`}>{link(host, `/v/${slug}`)}</div>
        <Wordmark />
      </div>
    </Frame>
  )
}
